import { MonitorIcon, MoonIcon, SunIcon, type LucideIcon } from "lucide-react";
import { useTheme } from "next-themes";
import { useSyncExternalStore } from "react";

import { Button } from "@bindery/ui/components/ui/button";

type ThemeChoice = "light" | "dark" | "system";

const NEXT_CHOICE: Record<ThemeChoice, ThemeChoice> = {
  light: "dark",
  dark: "system",
  system: "light",
};

const CHOICE_ICON: Record<ThemeChoice, LucideIcon> = {
  light: SunIcon,
  dark: MoonIcon,
  system: MonitorIcon,
};

const isThemeChoice = (value: string | undefined): value is ThemeChoice =>
  value === "light" || value === "dark" || value === "system";

const subscribeNever = () => () => undefined;

// next-themes' theme is undefined until hydration, so the server snapshot keeps
// prerendered markup and the first client render on the system icon.
const useHydrated = (): boolean =>
  useSyncExternalStore(
    subscribeNever,
    () => true,
    () => false,
  );

function themeToggleLabel(choice: ThemeChoice): string {
  return `Theme: ${choice}. Switch to ${NEXT_CHOICE[choice]}.`;
}

export function ModeToggle({
  className,
  iconClassName,
}: {
  className?: string;
  /** Sizes the icon; the Button's own rule only reaches an icon with no size class. */
  iconClassName?: string;
}) {
  const { theme, setTheme } = useTheme();
  const hydrated = useHydrated();
  const choice: ThemeChoice =
    hydrated && isThemeChoice(theme) ? theme : "system";
  const Icon = CHOICE_ICON[choice];
  const label = themeToggleLabel(choice);

  return (
    <Button
      variant="ghost"
      size="icon"
      className={className}
      title={label}
      onClick={() => setTheme(NEXT_CHOICE[choice])}
    >
      <Icon aria-hidden="true" className={iconClassName} />
      <span className="sr-only">{label}</span>
    </Button>
  );
}
